/**
 * Bill-invite client (spec 2026-07-22 §3/§5.2) — turns the table's open bill
 * into a shareable link for someone who never scanned the QR.
 *
 *   POST /v1/online-ordering/branches/{branchId}/tables/{tableId}/bill-invite
 *   Body: {displayName} → {groupCartId, inviteCode, participantToken, orderId, participant}
 *
 * The BFF binds a group cart to the table's active order, so the recipient's
 * existing `?inviteCode=` deep link → GroupJoinScreen → participant-token flow
 * authorizes them for the bill and its splits with no new machinery.
 *
 * Auth: API key only (`skipAuth: true`; X-API-KEY auto-attaches on
 * /v1/online-ordering paths) — same trust level as the active-order probe and
 * payer-session: the caller must already know branch + table from the QR.
 *
 * Errors the UI must speak to:
 *  - 404 → the table has no open bill right now.
 *  - 409 → the table's cart is bound to a different order (stale), or full.
 *
 * Idempotent: repeated calls join the same active cart and return the SAME
 * `inviteCode`; each caller gets their own participantToken + guestId.
 */

import { resolveSlug } from "@/lib/resolveSlug";

import { apiFetch } from "./http";
import type { GroupCartParticipant } from "./group-cart";

export interface BillInviteInput {
  displayName: string;
}

export interface BillInviteResponse {
  groupCartId: string;
  inviteCode: string;
  participantToken: string;
  orderId: string;
  participant: GroupCartParticipant;
}

export const billInviteApi = {
  create(
    branchId: string,
    tableId: string,
    input: BillInviteInput,
  ): Promise<BillInviteResponse> {
    return apiFetch<BillInviteResponse>(
      `/v1/online-ordering/branches/${encodeURIComponent(branchId)}/tables/${encodeURIComponent(tableId)}/bill-invite`,
      { method: "POST", body: input, skipAuth: true },
    );
  },
};

/**
 * The link a recipient opens: HomeScreen forwards `?inviteCode=` to GroupJoinScreen.
 *
 * MUST include the storefront base path (`/:slug`) on path-routed hosts
 * (order-staging.tappya.com, menu.tappya.com, …). A bare-origin link gives
 * `resolveSlug()` no first path segment, so BranchProvider never fetches the
 * tenant config and calls `setApiKey(null)` — every subsequent
 * /v1/online-ordering call then goes out with no `X-API-KEY` and the BFF
 * answers 401 "Not authenticated", including the join itself.
 *
 * We use the resolved basename rather than `window.location.pathname` so the
 * link always lands on HomeScreen (the only screen that forwards
 * `?inviteCode=` to /group/join), regardless of which screen the sharer was on.
 */
export function buildBillInviteUrl(inviteCode: string): string {
  const { basename } = resolveSlug();
  return `${window.location.origin}${basename}/?inviteCode=${encodeURIComponent(inviteCode)}`;
}
